import { type FC, useLayoutEffect, useState } from "react"
import styled from "styled-components"

import { type SubscriptionPaymentInterval } from "@forento/core/models/payment"
import { type FreePlatformPlan, type RegularPlatformPlan } from "@forento/core/models/platform"
import { getPlansAnnualDiscount } from "@forento/core/utilities/payment"

import PartialLoadingPage from "~/components/PartialLoadingPage"
import PaymentIntervalSelector from "~/components/PaymentIntervalSelector"
import { useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import CancelSubscriptionModal from "../CancelSubscriptionModal"
import Plan from "./Plan"

type Props = { platformPlan: FreePlatformPlan | RegularPlatformPlan }
const Regular: FC<Props> = ({ platformPlan }) => {
	const user = useUser()

	const [isMonthlySelected, setMonthlySelected] = useState(true)

	const plans = query.payment.creator.listPlans.useQuery()
	const currentPlan = query.payment.creator.getCurrentPlan.useQuery()

	const [isCancelModalOpen, setCancelModalOpen] = useState(false)
	const [submittingPlanId, setSubmittingPlanId] = useState<number | null>()

	useLayoutEffect(() => {
		setMonthlySelected(currentPlan.data?.price?.interval === "month")
	}, [currentPlan.data?.price?.interval])

	const reloadPlans = async () => {
		await Promise.all([plans.refetch(), currentPlan.refetch(), user.reload()])
	}

	const handleResumeSubscription = async () => {
		if (currentPlan == null) return
		setSubmittingPlanId(platformPlan.id)
		try {
			await trpc.payment.creator.resumePlan.mutate()
		} catch (error) {
			console.error(error)
		}
		await reloadPlans().catch(error => console.error(error))
		setSubmittingPlanId(undefined)
	}

	const handleChoosePlan = async (planId: number, paymentInterval: SubscriptionPaymentInterval) => {
		setSubmittingPlanId(planId)
		try {
			const { redirectUrl } = await trpc.payment.creator.setPlan.mutate({
				planId,
				paymentInterval,
				redirect: { cancel: routes.settings.billing.plans(), success: routes.settings.billing.plans() },
				source: "settings",
			})
			if (redirectUrl !== null) {
				window.location.assign(redirectUrl)
			}
		} catch (error) {
			console.error(error)
		}
		await reloadPlans().catch(error => console.error(error))
		setSubmittingPlanId(undefined)
	}

	if (plans.error || currentPlan.error) return <p>Failed to load plans.</p>
	if (plans.data === undefined || currentPlan.data === undefined) return <PartialLoadingPage />

	return (
		<>
			<CancelSubscriptionModal
				type="plan"
				isOpen={isCancelModalOpen}
				onClose={() => setCancelModalOpen(false)}
				onSubmitted={async () => {
					await reloadPlans()
					setCancelModalOpen(false)
				}}
			/>
			<Container>
				<PaymentIntervalSelector
					annualDiscount={getPlansAnnualDiscount(plans.data)}
					isMonthlySelected={isMonthlySelected}
					setMonthlySelected={setMonthlySelected}
				/>
				<PlansList>
					{plans.data.map(plan => (
						<Plan
							key={plan.id}
							plan={plan}
							currentPlan={currentPlan.data}
							platformPlan={platformPlan}
							isMonthly={isMonthlySelected}
							isSubmitting={submittingPlanId === plan.id}
							isDisabled={submittingPlanId !== undefined}
							onCancelSubscription={() => setCancelModalOpen(true)}
							onResumeSubscription={handleResumeSubscription}
							onSelectPlan={paymentInterval =>
								plan.id !== null && handleChoosePlan(plan.id, paymentInterval)
							}
						/>
					))}
				</PlansList>
			</Container>
		</>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const PlansList = styled.div`
	display: flex;
	gap: 16px;
`

export default Regular
